import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import type { ConsultSlotEntry } from "./consult_availability";

export default function BookedConsultDetails({ entry, onClose }: { entry: ConsultSlotEntry; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    const trigger = document.activeElement;
    dialog?.showModal();
    return () => {
      dialog?.close();
      if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus();
    };
  }, []);

  return createPortal(
    <dialog ref={dialogRef} aria-labelledby={titleId}
      className="fixed inset-0 m-auto max-h-[calc(100%-2rem)] w-[calc(100%-2rem)] max-w-md overflow-auto rounded-xl border border-neutral-200 bg-white p-5 text-neutral-900 shadow-xl backdrop:bg-neutral-950/55 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
      onCancel={(event) => { event.preventDefault(); onClose(); }} onClose={onClose}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
      }}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">Booked consult</p>
          <h2 id={titleId} className="mt-1 text-lg font-semibold">{entry.bookedPatientName || "Patient details unavailable"}</h2>
        </div>
        <button type="button" className="app-button-secondary" onClick={onClose}>Close</button>
      </div>
      <dl className="my-5 space-y-3 text-sm">
        <div><dt className="text-neutral-500 dark:text-neutral-400">When</dt><dd>{entry.scheduleLabel} · Arizona time</dd></div>
        <div><dt className="text-neutral-500 dark:text-neutral-400">Deva location</dt><dd>{entry.devaLocationLabel || "Not recorded"}</dd></div>
        {entry.bookedConsultTypeLabel ? <div><dt className="text-neutral-500 dark:text-neutral-400">Visit type</dt><dd>{entry.bookedConsultTypeLabel}</dd></div> : null}
      </dl>
      <div className="flex flex-wrap gap-2">
        {entry.bookedPatientPath ? <a href={entry.bookedPatientPath} target="_blank" rel="noopener noreferrer" className="app-button-primary">View patient</a> : null}
        {entry.bookedConsultPath ? <a href={entry.bookedConsultPath} target="_blank" rel="noopener noreferrer" className="app-button-secondary">View consult</a> : null}
      </div>
    </dialog>, document.body,
  );
}
